/**
 * The organisation as a tree, and that tree as a list of rows.
 *
 * The module-screens book draws Organisation twice — once as a chart, once as a
 * list — and is emphatic that they are the same object seen twice, not two
 * screens. So the shape is built once here and both views read it: the chart
 * walks `root.children`, the list walks `flattenOrgTree`. A headcount that
 * differed between the two would be the tell that they had drifted apart.
 *
 * Pure on purpose. Everything here is a function of the three arrays the
 * services already cache (employees, departments, employee groups) and nothing
 * touches React, so the counting — which is the part that is actually easy to
 * get wrong — is unit-testable on its own.
 */

/** One level of indent in the list view. The book's figure, not a guess. */
export const INDENT_PX = 26;

/**
 * How many individuals a leaf unit lists before it stops naming them.
 *
 * The book: a long team "truncates to a count rather than running for forty
 * rows". The point of opening a unit is to see who is in it, not to turn the
 * hierarchy back into the People table — past a handful of faces the reader has
 * stopped reading names anyway, and the remainder is better said as a number.
 */
export const DEFAULT_PEOPLE_LIMIT = 8;

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const id = (v) => (v == null ? "" : String(v?._id ?? v));

const fullName = (e) => `${e?.fname || ""} ${e?.lname || ""}`.trim();

const byName = (a, b) => a.name.localeCompare(b.name);

/** A date value as a UTC calendar day, or null. */
const utcDay = (value) => {
  if (!value) return null;
  const d = new Date(String(value).slice(0, 10));
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * "Starts 1 Apr", but only for someone who has not started yet.
 *
 * Both sides are normalised to a UTC calendar day before comparing: a joining
 * date is a day, not an instant, and comparing it as a timestamp puts someone
 * starting today on the wrong side of the line depending on the reader's
 * timezone. The month is spelled from a table rather than taken from
 * toLocaleDateString, which renders September as "Sept" under current ICU.
 */
export const startsNote = (joiningDate, today = new Date()) => {
  const start = utcDay(joiningDate);
  if (!start) return null;
  const now = utcDay(today.toISOString());
  if (!now || start.getTime() <= now.getTime()) return null;
  return `Starts ${start.getUTCDate()} ${SHORT_MONTHS[start.getUTCMonth()]}`;
};

const toPerson = (e, today) => ({
  id: id(e),
  name: fullName(e) || "—",
  role: e?.designation || "—",
  profileImage: e?.profileImage || null,
  // Absent for a non-admin reader: the directory projection does not carry
  // joiningDate. No note is the right answer then, not a fabricated one.
  starts: startsNote(e?.joiningDate, today),
});

/**
 * Build the tree.
 *
 * `teams` is the employee-group list. A group is only placed under a department
 * when it actually says which department it belongs to; the current backend
 * model has no such field, so in practice no groups are placed and departments
 * hold their people directly. That is deliberate — inventing a parent for a
 * group would put a wrong structure on the screen, and the structure is the
 * whole point of this screen. `unplacedTeams` reports how many were dropped.
 */
export const buildOrgTree = ({
  employees = [],
  departments = [],
  teams = [],
  today = new Date(),
} = {}) => {
  const active = (Array.isArray(employees) ? employees : []).filter(
    (e) => e && e.isActive !== false
  );
  const empById = new Map(active.map((e) => [id(e), e]));
  const lead = (ids) => {
    const first = (Array.isArray(ids) ? ids : []).map(id).find((x) => empById.has(x));
    if (!first) return null;
    const e = empById.get(first);
    return { id: first, name: fullName(e) || "—", role: e?.designation || null };
  };

  const deptList = (Array.isArray(departments) ? departments : []).filter(Boolean);
  const deptIds = new Set(deptList.map(id));

  // Teams first: their members are then not repeated as loose people on the
  // department, or the same person is counted in two places on one screen.
  const teamsByDept = new Map();
  let unplacedTeams = 0;
  (Array.isArray(teams) ? teams : []).filter(Boolean).forEach((t) => {
    const parent = id(t.department);
    if (!parent || !deptIds.has(parent)) {
      unplacedTeams += 1;
      return;
    }
    const members = (Array.isArray(t.employee) ? t.employee : [])
      .map(id)
      .filter((x) => empById.has(x));
    const teamLead = lead(t.manager);
    const list = teamsByDept.get(parent) || [];
    list.push({
      id: id(t),
      kind: "team",
      name: t.name || "Untitled team",
      lead: teamLead,
      headcount: members.length,
      children: [],
      memberIds: members,
      people: members
        .filter((x) => x !== teamLead?.id)
        .map((x) => toPerson(empById.get(x), today))
        .sort(byName),
    });
    teamsByDept.set(parent, list);
  });

  const membersOf = new Map();
  const unassigned = [];
  active.forEach((e) => {
    const d = id(e.department);
    if (d && deptIds.has(d)) {
      const list = membersOf.get(d) || [];
      list.push(e);
      membersOf.set(d, list);
    } else {
      unassigned.push(e);
    }
  });

  const departmentUnits = deptList
    .map((d) => {
      const key = id(d);
      const members = membersOf.get(key) || [];
      const children = (teamsByDept.get(key) || []).sort(byName);
      const inTeam = new Set(children.flatMap((c) => c.memberIds));
      const deptLead = lead(d.manager);
      return {
        id: key,
        kind: "department",
        name: d.name || "Untitled department",
        lead: deptLead,
        headcount: members.length,
        children: children.map(({ memberIds, ...rest }) => rest),
        people: members
          .filter((e) => id(e) !== deptLead?.id && !inTeam.has(id(e)))
          .map((e) => toPerson(e, today))
          .sort(byName),
      };
    })
    .sort(byName);

  /**
   * People with no department are still people. Left out, the chart's nodes
   * would not add up to the company total and the reader would be left to
   * wonder which number was wrong — so they get a node of their own rather
   * than quietly disappearing. It is not a vacancy, so it is not dashed.
   */
  if (unassigned.length) {
    departmentUnits.push({
      id: "__unassigned__",
      kind: "unassigned",
      name: "No department",
      lead: null,
      headcount: unassigned.length,
      children: [],
      people: unassigned.map((e) => toPerson(e, today)).sort(byName),
    });
  }

  // The top of the reporting chain: whoever reports to nobody inside the org.
  // Only when there is exactly one — two would be a claim about who runs the
  // company that the data does not actually make.
  const tops = active.filter((e) => {
    const mgr = id(e.reportingTo);
    return !mgr || !empById.has(mgr) || mgr === id(e);
  });
  const head = tops.length === 1 ? tops[0] : null;

  return {
    root: {
      id: "__company__",
      kind: "company",
      name: "Whole company",
      lead: head ? { id: id(head), name: fullName(head) || "—", role: head.designation || null } : null,
      headcount: active.length,
      children: departmentUnits,
      people: [],
    },
    unplacedTeams,
  };
};

/** Header line counts, taken off the built tree so they cannot disagree with it. */
export const countOrgTree = (root) => {
  const departments = (root?.children || []).filter((u) => u.kind === "department").length;
  const teams = (root?.children || []).reduce((n, u) => n + (u.children?.length || 0), 0);
  return { departments, teams, people: root?.headcount || 0 };
};

const containsLabel = (unit) => {
  const n = unit.children?.length || 0;
  if (!n) return "";
  return `${n} ${n === 1 ? "team" : "teams"}`;
};

/** Every unit id in the tree, for "Expand all". */
export const allUnitIds = (root) => {
  const out = [];
  const walk = (units) =>
    units.forEach((u) => {
      out.push(u.id);
      walk(u.children || []);
    });
  walk(root?.children || []);
  return out;
};

/**
 * The tree as the rows the list view draws.
 *
 * One row per unit, expanded to whatever depth the reader opened — closed units
 * contribute exactly one row, which is what keeps this a hierarchy being read
 * rather than the People table with a department column. Individuals appear
 * only inside an opened leaf, and only up to `peopleLimit`; the rest become a
 * single "N more in X" row.
 *
 * The whole-company summary is the last row rather than a separate element, so
 * the total sits in the same column as the numbers it is the sum of.
 */
export const flattenOrgTree = (root, { expanded, peopleLimit = DEFAULT_PEOPLE_LIMIT } = {}) => {
  const isOpen = (unitId) =>
    expanded instanceof Set ? expanded.has(unitId) : Boolean(expanded?.[unitId]);
  const rows = [];

  const walk = (units, depth) => {
    units.forEach((unit) => {
      const open = isOpen(unit.id);
      const people = unit.people || [];
      const children = unit.children || [];
      rows.push({
        rowId: `unit:${unit.id}`,
        kind: "unit",
        unitKind: unit.kind,
        depth,
        id: unit.id,
        name: unit.name,
        lead: unit.lead,
        headcount: unit.headcount,
        contains: containsLabel(unit),
        expandable: children.length > 0 || people.length > 0,
        expanded: open,
      });
      if (!open) return;
      walk(children, depth + 1);
      people.slice(0, peopleLimit).forEach((p) => {
        rows.push({
          rowId: `person:${unit.id}:${p.id}`,
          kind: "person",
          depth: depth + 1,
          id: p.id,
          name: p.name,
          role: p.role,
          profileImage: p.profileImage,
          starts: p.starts,
        });
      });
      if (people.length > peopleLimit) {
        const remaining = people.length - peopleLimit;
        rows.push({
          rowId: `more:${unit.id}`,
          kind: "more",
          depth: depth + 1,
          id: unit.id,
          count: remaining,
          label: `${remaining} more in ${unit.name}`,
        });
      }
    });
  };

  walk(root?.children || [], 0);

  rows.push({
    rowId: "summary",
    kind: "summary",
    depth: 0,
    name: "Whole company",
    lead: root?.lead || null,
    headcount: root?.headcount || 0,
  });

  return rows;
};
